import { Box, Checkbox, FormControl, FormErrorMessage, FormLabel, useDisclosure, useToast } from "@chakra-ui/react";
import { useFormik } from "formik";
import { useContext, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import { Link, useLocation, useNavigate } from "react-router";

import { EquidefiMarkdown } from "@equidefi/portals/components/Markdown";
import CommonStockForm from "@equidefi/portals/components/investments/CommonStockForm";
import { VaultContext } from "@equidefi/portals/context/VaultProvider";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useSubmitSubscription } from "@equidefi/portals/hooks/useInvestments";
import {
  offeringHidesSecurityLabel,
  offeringInvestorMinimumUnits,
  offeringIsIPO,
  offeringIsRegS,
  offeringIsTypeRegA,
} from "@equidefi/shared";
import { BannerAlert, ConfirmModal, Heading, Input, Modal, RadioGroup, Text } from "@equidefi/ui";

import DocumentReview from "@/components/subscription/DocumentReview";
import { InvestmentRoundingModal } from "@/components/subscription/InvestmentRoundingModal";
import { isConvertibleOffering, isRegD506COffering } from "@/helpers";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";
import WorkflowStepError from "../WorkflowStepError";
import { subscriptionSchema } from "./schema";

const ACCREDITED_INVESTOR_URL = "https://www.sec.gov/education/capitalraising/building-blocks/accredited-investor";

export const WorkflowSubscription = () => {
  const { offering, user, investment, refetchInvestment } = useWorkflowContext();
  const { vault } = useContext(VaultContext);

  const location = useLocation();
  useWorkflowPosition(2);
  const toast = useToast();

  const [spinner, showSpinner] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [inputMode, setInputMode] = useState("USD");
  const [pendingSubmission, setPendingSubmission] = useState(null);
  useDocumentTitle(["Subscription", offering?.name, investment?.issuer?.name]);
  const navigate = useNavigate();
  const submitSubscription = useSubmitSubscription(investment?.id);

  const unitPrice = Number(offering?.unit_price);
  const isOfferingRegA = offeringIsTypeRegA(offering);
  const isOffering506c = isRegD506COffering(offering?.exemption_type);
  const isOfferingRegS = offeringIsRegS(offering);
  const isOfferingIPO = offeringIsIPO(offering);
  // Reg A and IPO both collect self-certified income/net worth for non-accredited
  // investors; IPO differs only in that it never blocks on the 10% threshold (see schema).
  const collectsIncomeInfo = isOfferingRegA || isOfferingIPO;
  const hideSecurityLabel = offeringHidesSecurityLabel(offering);

  const rejectionModal = useDisclosure();
  const roundingModal = useDisclosure();
  const brokerDealerModal = useDisclosure();

  const validationSchema = useMemo(() => {
    return subscriptionSchema({
      offering,
      investments: vault?.investments.filter(({ id }) => id !== investment?.id),
    });
  }, [offering, vault, investment]);

  const isExpedited = investment?.expedited;
  const isDefaultable = useMemo(() => Number(investment?.amount) > 0 || isExpedited, [investment, isExpedited]);

  const [acknowledged, setAcknowledged] = useState((!!location.state?.returnTo || isExpedited) ?? false);
  const isIndividualOrJoint = ["INDIVIDUAL", "JOINT"].includes(investment.vault?.ta_account_type);

  // Default income/net worth to a number (vs null) when the offering collects it, or when a
  // prior income value already exists on the vault; otherwise leave null (fields aren't shown).
  const hasStoredIncome = Boolean(investment?.vault?.annual_income);
  const defaultIncomeFields = hasStoredIncome || collectsIncomeInfo;

  const initialValues = {
    accredited: isDefaultable ? investment?.vault.accredited : null,
    broker_dealer: isDefaultable ? investment?.vault?.broker_dealer : null,
    annual_income: defaultIncomeFields ? (isDefaultable ? investment?.vault?.annual_income || 0 : 0) : null,
    net_worth: collectsIncomeInfo ? (isDefaultable ? investment?.vault?.net_worth || 0 : 0) : null,
    quantity: investment?.quantity || 0,
    amount: investment?.quantity * unitPrice || 0,
  };

  const handleCustomSubmit = async (e) => {
    // Prevent default form submission to avoid page refresh
    if (e) {
      e.preventDefault();
    }

    // The Continue button is visually disabled while the form is invalid, but pressing Enter
    // still fires the form's submit handler and skips that disabled state. Gate the keyboard
    // path on the same validity check so it can't submit with missing radio selections (EPP-3078).
    if (isSubmitDisabled) {
      return;
    }

    // In USD mode with decimal quantity, bypass Formik validation and submit anyway
    const hasQuantityDecimalError =
      inputMode === "USD" &&
      errors.quantity &&
      typeof errors.quantity === "string" &&
      errors.quantity.includes("must not be a decimal");

    if (hasQuantityDecimalError) {
      // Manually call submitForm with current values
      await submitForm(values);
    } else {
      // Use Formik's normal validation and submission
      handleSubmit(e);
    }
  };

  const submitForm = async ({ amount, quantity, accredited, annual_income, net_worth, broker_dealer }) => {
    showSpinner(true);

    try {
      // Check if this is a 506(c) offering with non-accredited investor
      // Reg S investors can proceed even if not accredited
      if (isOffering506c && !isOfferingRegS && accredited === false) {
        await submitSubscription.mutateAsync({
          accredited,
          broker_dealer,
          annual_income,
          quantity,
          amount,
          net_worth,
          user_id: user.id,
        });

        // Refetch investment data
        await refetchInvestment();

        // Show rejection modal and stay on subscription page
        rejectionModal.onOpen();
        showSpinner(false);
        return;
      }

      // Validate USD amount divides evenly into shares
      if (inputMode === "USD" && amount > 0) {
        const calculatedShares = amount / unitPrice;
        const isWholeNumber = Number.isInteger(calculatedShares);

        if (!isWholeNumber) {
          // Show rounding modal
          const minInvestmentAmount = offeringInvestorMinimumUnits(offering) * unitPrice;

          if (amount < minInvestmentAmount) {
            showSpinner(false);
            return;
          }

          const roundedUp = Math.ceil(calculatedShares) * unitPrice;
          const roundedDown = Math.floor(calculatedShares) * unitPrice;
          let suggestedAmount = 0;
          if (roundedUp <= amount) {
            suggestedAmount = Math.max(roundedUp, minInvestmentAmount);
          } else {
            suggestedAmount = Math.max(roundedDown, minInvestmentAmount);
          }
          if (suggestedAmount > amount) {
            suggestedAmount = roundedDown;
          }

          const submissionData = {
            accredited,
            broker_dealer,
            annual_income,
            net_worth,
            originalAmount: amount,
            suggestedAmount,
          };

          setPendingSubmission(submissionData);
          roundingModal.onOpen();
          showSpinner(false);
          return;
        }
      }

      // Proceed with submission
      await proceedWithSubmission({
        accredited,
        broker_dealer,
        annual_income,
        quantity,
        amount,
        net_worth,
      });
    } catch (err) {
      toast({
        description: "Sorry! Something went wrong updating your subscription",
        isClosable: true,
        status: "error",
      });
      console.error(err);
      showSpinner(false);
    }
  };

  const proceedWithSubmission = async ({ accredited, broker_dealer, annual_income, quantity, amount, net_worth }) => {
    try {
      let page = "payment";

      const differentValues = Number(initialValues?.amount) > 0 && Number(initialValues?.amount) !== Number(amount);

      if (location.state?.returnTo) {
        if (!differentValues) {
          page = location.state.returnTo;
        }
      }

      if (!location.state?.returnTo && !investment.payment_editable) {
        page = "address";
      }

      await submitSubscription.mutateAsync({
        accredited,
        broker_dealer,
        annual_income,
        quantity,
        amount,
        net_worth,
        user_id: user.id,
      });

      // Refetch investment data first, then navigate
      await refetchInvestment();

      // Mark as navigating to prevent UI flashing during navigation
      setIsNavigating(true);

      // When returnTo is "payment", clear it to avoid navigation loops
      // (payment page shouldn't navigate back to itself after submission)
      const shouldClearReturnTo = location.state?.returnTo === "payment";
      const navigationState = shouldClearReturnTo ? {} : { returnTo: location.state?.returnTo };

      navigate(`/offerings/${offering.slug}/${investment?.id}/${page}`, {
        state: navigationState,
      });
    } catch (err) {
      toast({
        description: "Sorry! Something went wrong updating your subscription",
        isClosable: true,
        status: "error",
      });
      console.error(err);
    } finally {
      showSpinner(false);
    }
  };

  const handleRoundingModalContinue = async (adjustedAmount) => {
    roundingModal.onClose();

    if (adjustedAmount === null) {
      // User chose to enter different amount - just close modal
      setPendingSubmission(null);
      return;
    }

    // User chose to round up - update values and submit
    showSpinner(true);
    const adjustedQuantity = adjustedAmount / unitPrice;

    setValues((current) => ({
      ...current,
      amount: adjustedAmount,
      quantity: adjustedQuantity,
    }));

    // Proceed with the submission using adjusted values
    await proceedWithSubmission({
      accredited: pendingSubmission.accredited,
      broker_dealer: pendingSubmission.broker_dealer,
      annual_income: pendingSubmission.annual_income,
      quantity: adjustedQuantity,
      amount: adjustedAmount,
      net_worth: pendingSubmission.net_worth,
    });

    setPendingSubmission(null);
  };

  const handleRoundingModalCancel = () => {
    roundingModal.onClose();
    setPendingSubmission(null);
  };

  const { handleSubmit, setFieldValue, setValues, handleBlur, validateForm, values, errors, isValid, touched } =
    useFormik({
      initialValues: initialValues,
      enableReinitialize: !spinner,
      validationSchema: validationSchema,
      onSubmit: submitForm,
      validateOnChange: true,
      validateOnBlur: true,
      validateOnMount: true,
    });

  const isAccredited = values?.accredited;

  const isSubmitDisabled = useMemo(() => {
    // When in USD mode, allow submission even if quantity is not a whole number
    // The modal will handle rounding on submission
    if (inputMode === "USD") {
      // Check if the only error is the quantity decimal validation
      const hasQuantityDecimalError =
        errors.quantity && typeof errors.quantity === "string" && errors.quantity.includes("must not be a decimal");

      // If we have the decimal error, check if there are other errors
      if (hasQuantityDecimalError) {
        const otherErrors = Object.keys(errors).filter((key) => key !== "quantity");
        const customIsValid = otherErrors.length === 0 && values.amount > 0;

        if (collectsIncomeInfo && !values.accredited) return !acknowledged || !customIsValid;
        return !customIsValid;
      }
    }

    // Keep the button disabled while navigating to the next step to prevent a double submit.
    if (isNavigating) {
      return true;
    }

    // Default validation for Shares mode and when no decimal error in USD mode
    if (collectsIncomeInfo && !values.accredited) return !acknowledged || !isValid;
    return !isValid;
  }, [collectsIncomeInfo, acknowledged, isValid, inputMode, errors, values.amount, values.accredited, isNavigating]);

  if (!investment) return null;

  if (!investment.payment_editable) {
    return (
      <WorkflowStepError
        title="Investment Amount"
        text="Your subscription has already been entered and a payment has been
          submitted, you can no longer edit the subscription amount for this
          investment"
      />
    );
  }

  return (
    <WorkflowContainer
      title="Investment Amount"
      onContinue={handleCustomSubmit}
      buttonProps={{ isDisabled: isSubmitDisabled, isLoading: spinner }}>
      <WorkflowForm onSubmit={handleCustomSubmit}>
        <FormControl mb={2}>
          <BannerAlert
            status="info"
            title="Accredited Investor"
            titleLayout="inline"
            my={4}
            description={
              isOfferingRegA ? (
                <Text textStyle="body2" mb={0}>
                  Accredited investor means you have a net worth greater than $1 million (not including your primary
                  residence) or an income greater than $200,000 in the last two years as an individual, or $300,000 as a
                  joint account. If you are not accredited you can still invest, but as long as the purchase does not
                  exceed 10% of the investor's net worth or annual income, whichever is greater.
                </Text>
              ) : (
                <Text textStyle="body2" mb={0}>
                  Accredited investor means you have a net worth greater than $1 million (not including your primary
                  residence) or an income greater than $200,000 in the last two years as an individual, or $300,000 as a
                  joint account.
                </Text>
              )
            }
          />
          <FormLabel>
            <Heading fontWeight="bold" mb="0">
              Are you an accredited investor?{" "}
              {isOfferingRegA || isOfferingRegS || isOfferingIPO ? "If not, don't worry, you can still invest!" : ""}
            </Heading>
          </FormLabel>

          <RadioGroup
            isDisabled={isExpedited}
            value={values.accredited}
            onChange={(value) => {
              setFieldValue("accredited", value);
            }}
            direction="horizontal"
            options={[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ]}
          />
        </FormControl>
        <Text textStyle="context">
          Please see{" "}
          <Text
            textStyle="context"
            as={Link}
            textDecor="underline"
            _hover={{ color: "blue.700" }}
            color="blue.600"
            to={ACCREDITED_INVESTOR_URL}
            rel="noopener noreferrer"
            target="_blank">
            What is an Accredited Investor
          </Text>{" "}
          to make sure you make the right selection.
        </Text>

        {!isOfferingRegA && !isOffering506c && !isOfferingRegS && !isOfferingIPO && isAccredited === false && (
          <BannerAlert
            status="error"
            description="You must be an accredited investor to invest in this offering."
            my={4}
          />
        )}

        <FormControl mb={2} mt={6}>
          <FormLabel>
            <Heading fontWeight="bold" mb="0">
              Are you affiliated with a broker-dealer?
            </Heading>
          </FormLabel>
          <Text textStyle="body2" mb={2}>
            In other words, do you or a relative work for a broker-dealer?
          </Text>

          <RadioGroup
            isDisabled={isExpedited}
            value={values.broker_dealer}
            onChange={(value) => {
              if (value === true) {
                brokerDealerModal.onOpen();
              } else {
                setFieldValue("broker_dealer", value);
              }
            }}
            direction="horizontal"
            options={[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ]}
          />
        </FormControl>
        <Text textStyle="context" mb={6}>
          Please see{" "}
          <Text
            textStyle="context"
            as={Link}
            textDecor="underline"
            _hover={{ color: "blue.700" }}
            color="blue.600"
            to="https://www.finra.org/investors/learn-to-invest/choosing-investment-professional/brokers"
            rel="noopener noreferrer"
            target="_blank">
            What Is An Affiliate of a Broker-Dealer
          </Text>{" "}
          to make sure you make the right selection.
        </Text>

        {collectsIncomeInfo && values?.accredited !== true && (
          <>
            <Heading fontWeight="bold" mb="0">
              Please provide the following information
            </Heading>
            <Input
              label={isIndividualOrJoint ? "Annual Income" : "Revenue"}
              isInvalid={errors.annual_income && touched.annual_income}
              maxW="50%"
              as={NumericFormat}
              allowNegative={false}
              allowLeadingZeros={false}
              thousandSeparator
              fixedDecimalScale={false}
              decimalScale={0}
              prefix="$"
              name="annual_income"
              aria-label={isIndividualOrJoint ? "Annual Income" : "Revenue"}
              value={values.annual_income}
              onValueChange={(...args) => {
                const [{ floatValue }] = args;
                setFieldValue("annual_income", floatValue);
                validateForm();
              }}
              onFocus={() => {
                if (Number(values.annual_income) === 0) {
                  setFieldValue("annual_income", "");
                }
              }}
              error={errors.annual_income}
              isRequired={true}
              onBlur={handleBlur}
              isDisabled={isExpedited}
            />
            <Input
              label={isIndividualOrJoint ? "Net Worth" : "Net Assets"}
              isInvalid={errors.net_worth && touched.net_worth}
              maxW="50%"
              as={NumericFormat}
              allowNegative={false}
              allowLeadingZeros={false}
              thousandSeparator
              fixedDecimalScale={false}
              decimalScale={0}
              prefix="$"
              name="net_worth"
              aria-label={isIndividualOrJoint ? "Net Worth" : "Net Assets"}
              value={values.net_worth}
              onValueChange={(...args) => {
                const [{ floatValue }] = args;
                setFieldValue("net_worth", floatValue);
                validateForm();
              }}
              onFocus={() => {
                if (Number(values.net_worth) === 0) {
                  setFieldValue("net_worth", "");
                }
              }}
              error={errors.net_worth}
              isRequired={true}
              onBlur={handleBlur}
              isDisabled={isExpedited}
            />

            {isOfferingRegA && (
              <Text textStyle="context" mb={3}>
                For non-accredited investors, investments are limited to a maximum of 10% of net worth or annual income,
                whichever is greater, if the Investor is a natural person; or 10% of its revenues or net assets,
                whichever is greater, for its most recently completed fiscal year, if the Investor is a non-natural
                person.
              </Text>
            )}

            <FormControl mb="4" isInvalid={touched.quantity && !acknowledged}>
              <Checkbox
                variant="solid"
                isDisabled={isExpedited}
                isChecked={acknowledged}
                isRequired
                name="acknowledged"
                size="lg"
                onChange={() => setAcknowledged((prev) => !prev)}>
                I acknowledge that I am self-certifying these values.
              </Checkbox>
              <FormErrorMessage>Required</FormErrorMessage>
            </FormControl>
          </>
        )}

        {offering?.subscription_message && (
          <Box mb={4} p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200" bg="gray.50">
            <EquidefiMarkdown>{offering.subscription_message}</EquidefiMarkdown>
          </Box>
        )}

        <>
          <Heading textStyle="h1" fontWeight="bold" mb={2}>
            Investment Amount
          </Heading>

          {!hideSecurityLabel && !isConvertibleOffering(offering.stock_type) && (
            <RadioGroup
              value={inputMode}
              onChange={(mode) => {
                setInputMode(mode);
                setValues({
                  ...values,
                  quantity: 0,
                  amount: 0,
                });
              }}
              direction="horizontal"
              options={[
                { value: "USD", label: "USD" },
                { value: "Shares", label: offering.security_type_plural },
              ]}
            />
          )}

          <CommonStockForm
            values={values}
            handleBlur={handleBlur}
            setValues={setValues}
            errors={errors}
            touched={touched}
            offering={offering}
            inputMode={inputMode}
          />
        </>
        <DocumentReview documents={offering.documents} />
      </WorkflowForm>

      {/* Non-accredited 506(c) rejection modal */}
      <Modal
        isOpen={rejectionModal.isOpen}
        onClose={rejectionModal.onClose}
        title="Investment Not Permitted"
        contentProps={{
          maxW: { base: "full", md: "540px" },
          w: { base: "full", md: "540px" },
        }}
        buttons={[
          {
            label: "Go Back",
            action: rejectionModal.onClose,
            colorScheme: "purple",
            variant: "solid",
          },
          {
            label: "Learn More",
            action: () => {
              window.open(ACCREDITED_INVESTOR_URL, "_blank", "noopener,noreferrer");
            },
            colorScheme: "purple",
            variant: "outline",
          },
        ]}>
        <BannerAlert status="error" description="This investment is only available to Accredited Investors." mb={4} />
        <Text textStyle="body2" mb={0}>
          Please see{" "}
          <Text
            textStyle="body2"
            as={Link}
            textDecor="none"
            _hover={{ color: "blue.600" }}
            color="blue"
            to={ACCREDITED_INVESTOR_URL}
            rel="noopener noreferrer"
            target="_blank">
            What is an Accredited Investor
          </Text>{" "}
          to learn more about the requirements.
        </Text>
      </Modal>

      <ConfirmModal
        isOpen={brokerDealerModal.isOpen}
        onCancel={() => {
          setFieldValue("broker_dealer", false);
          brokerDealerModal.onClose();
        }}
        onConfirm={() => {
          setFieldValue("broker_dealer", true);
          brokerDealerModal.onClose();
        }}
        title="Confirm Your Selection"
        description="Please read the following to ensure you made the right selection."
        cancelText="Change to No"
        confirmText="Continue with Yes"
        width="540px">
        <BannerAlert
          status="warning"
          title="Are you affiliated with a Broker-Dealer?"
          my={4}
          description="Being affiliated with a broker-dealer means that you either work for a broker-dealer or you have a relative that works for a broker-dealer. Having a brokerage account does not on its own constitute an affiliation with a broker-dealer."
        />
      </ConfirmModal>

      {/* Investment amount rounding modal */}
      {pendingSubmission && (
        <InvestmentRoundingModal
          isOpen={roundingModal.isOpen}
          onClose={handleRoundingModalCancel}
          onContinue={handleRoundingModalContinue}
          originalAmount={pendingSubmission.originalAmount}
          suggestedAmount={pendingSubmission.suggestedAmount}
          unitPrice={unitPrice}
          offering={offering}
          minInvestment={offeringInvestorMinimumUnits(offering) * unitPrice}
        />
      )}
    </WorkflowContainer>
  );
};
